import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    CircularProgress,
} from "@mui/material";
import { Delete, Edit, Plus } from 'lucide-react';
import {
    getViewRoom,
    createRoom,
    updateRoom,
    deleteRoom,
} from "../../../../../../api/services/hostService"; 
import { useUserContext } from "../../../../../protectedRoute/UserContext";
import { Button } from "../../../../../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../../../../../../components/ui/dialog";
import { Input } from "../../../../../../components/ui/input";
import { Label } from "../../../../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../../components/ui/select";
import { Textarea } from "../../../../../../components/ui/textarea";

interface Room {
    id: number;
    room_number: string;
    description?: string | null; // Added description field
    capacity: number;
    price_per_room: number;
    property?: string;
}

interface CreateAvailabilityProps {
    open: boolean;
    onClose: () => void;
    onRoomCreated: () => void;
}

const CreateAvailability: React.FC<CreateAvailabilityProps> = ({ open, onClose, onRoomCreated }) => {
    const [editingRoom, setEditingRoom] = useState<Room | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [customDescription, setCustomDescription] = useState("");
    const { state, dispatch } = useUserContext();
    const { propertyID } = state;

    useEffect(() => {
        if (open) {
            setEditingRoom({ id: 0, room_number: "", capacity: undefined as any, price_per_room: undefined as any, description: null });
            setIsCreating(true);
            setShowErrors(false);
            setCustomDescription("");
        }
    }, [open]);

    const finalDescription = editingRoom?.description === "Custom" ? customDescription.trim() : (editingRoom?.description ?? "");

    const fieldErrors = {
        room_number: !editingRoom?.room_number?.trim()
            ? "Room number is required"
            : !/^[0-9]{3}$/.test(editingRoom.room_number)
            ? "Must be a 3-digit number (e.g., 101)"
            : null,
        description: !editingRoom?.description?.trim()
            ? "Description is required"
            : editingRoom.description === "Custom" && !customDescription.trim()
            ? "Please enter a custom description"
            : null,
        capacity: !editingRoom?.capacity || editingRoom.capacity <= 0 ? "Capacity must be at least 1" : null,
        price_per_room: !editingRoom?.price_per_room || editingRoom.price_per_room <= 0
            ? "Price is required"
            : editingRoom.price_per_room > 1000
            ? "Price cannot exceed ₹1000"
            : null,
    };

    const canSave = editingRoom &&
        editingRoom.room_number && /^[0-9]{3}$/.test(editingRoom.room_number) &&
        editingRoom.capacity !== undefined && editingRoom.capacity > 0 &&
        editingRoom.price_per_room !== undefined && editingRoom.price_per_room > 0 && editingRoom.price_per_room <= 1000 &&
        editingRoom.description && editingRoom.description.trim() !== '' &&
        (editingRoom.description !== "Custom" || customDescription.trim() !== '');

    const handleDialogClose = () => {
        onClose();
        setEditingRoom(null);
        setIsCreating(false);
    };

    const handleSaveRoom = async () => {
        setShowErrors(true);
        if (!canSave) return;
        if (editingRoom && propertyID) {
            try {
                if (isCreating) {
                    await createRoom({
                        property: propertyID,
                        room_number: editingRoom.room_number,
                        description: finalDescription,
                        capacity: editingRoom.capacity,
                        price_per_room: editingRoom.price_per_room,
                    });
                    onRoomCreated();
                } else {
                    await updateRoom(editingRoom?.id, {
                        property: propertyID,
                        room_number: editingRoom.room_number,
                        description: finalDescription,
                        capacity: editingRoom.capacity,
                        price_per_room: editingRoom.price_per_room,
                    });
                    onRoomCreated();
                }
            } catch (error) {
                console.error("Failed to save room:", error);
            } finally {
                handleDialogClose();
            }
        }
    };

    const roomDescriptionOptions = [
        "Spacious room with a comfortable bed, wardrobe, and study desk — ideal for professionals.",
        "Well-ventilated room with attached bathroom, WiFi, and daily housekeeping.",
        "Cozy room with ample storage, ceiling fan, and 24/7 security.",
        "Bright and airy room with individual locker, WiFi, and common kitchen access.",
        "Affordable room with bunk beds, locker, and access to shared bathroom facilities.",
        "Fully furnished room with AC, wardrobe, and study table — perfect for students.",
        "Clean and well-maintained room with CCTV security and common amenities.",
        "Custom",
    ];

    return (
        <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[500px] bg-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Plus className="h-5 w-5 text-primary" />
              {isCreating ? "Add New Room" : "Update Room"}
            </DialogTitle>
            <DialogDescription>
              {isCreating ? "Add your room details and set availability to start earning" : "Update your room details"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            <div className="space-y-2">
                <Label htmlFor="roomNumber">Room Number <span className="text-red-500">*</span></Label>
                <Input
                  id="roomNumber"
                  placeholder="e.g., 101"
                  value={editingRoom?.room_number}
                  onChange={(e) => {
                      if(editingRoom) setEditingRoom({...editingRoom, room_number: e.target.value});
                  }}
                  className={`bg-background ${showErrors && fieldErrors.room_number ? "border-red-500" : ""}`}
                />
                {showErrors && fieldErrors.room_number && <p className="text-red-500 text-xs">{fieldErrors.room_number}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description <span className="text-red-500">*</span></Label>
                <Select
                    value={editingRoom?.description || ""}
                    onValueChange={(v) => {
                        if (editingRoom) setEditingRoom({...editingRoom, description: v});
                        if (v !== "Custom") setCustomDescription("");
                    }}
                >
                  <SelectTrigger className={`bg-background ${showErrors && fieldErrors.description ? "border-red-500" : ""}`}>
                    <SelectValue placeholder="Select description" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border z-50">
                    {roomDescriptionOptions.map(option => <SelectItem key={option} value={option}>{option}</SelectItem>)}
                  </SelectContent>
                </Select>
                {editingRoom?.description === "Custom" && (
                  <Textarea
                    placeholder="Enter custom description..."
                    value={customDescription}
                    onChange={(e) => setCustomDescription(e.target.value)}
                    className="bg-background resize-none mt-2"
                    rows={3}
                  />
                )}
                {showErrors && fieldErrors.description && <p className="text-red-500 text-xs">{fieldErrors.description}</p>}
              </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="roomCapacity">Capacity (persons) <span className="text-red-500">*</span></Label>
                <Input
                  id="roomCapacity"
                  type="number"
                  min="1"
                  placeholder="e.g., 2"
                  value={editingRoom?.capacity ?? ""}
                  onChange={(e) => {
                      if(editingRoom) setEditingRoom({...editingRoom, capacity: +e.target.value});
                  }}
                  className={`bg-background ${showErrors && fieldErrors.capacity ? "border-red-500" : ""}`}
                />
                {showErrors && fieldErrors.capacity && <p className="text-red-500 text-xs">{fieldErrors.capacity}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="roomPrice">Price per night (₹) <span className="text-red-500">*</span></Label>
                <Input
                  id="roomPrice"
                  type="number"
                  placeholder="e.g., 800"
                  value={editingRoom?.price_per_room ?? ""}
                  onChange={(e) => {
                      if(editingRoom) setEditingRoom({...editingRoom, price_per_room: +e.target.value});
                  }}
                  className={`bg-background ${showErrors && fieldErrors.price_per_room ? "border-red-500" : ""}`}
                />
                {showErrors && fieldErrors.price_per_room && <p className="text-red-500 text-xs">{fieldErrors.price_per_room}</p>}
              </div>
            </div>
          </div>

          <DialogFooter className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={handleDialogClose}>
              Cancel
            </Button>
            <Button onClick={handleSaveRoom}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
};

export default CreateAvailability;